import { mat3, vec3 } from 'gl-matrix';
import { DicomImageSlice } from '~/domain/dicom/dicomImageSlice';

export interface SliceStackFrame {
    /** The slices, ordered along the stack. */
    ordered: DicomImageSlice[];
    /** Each ordered slice's position along {@link stepDir}, from the first. */
    along: number[];
    /** In-plane directions, from ImageOrientationPatient. */
    rowDir: vec3;
    colDir: vec3;
    /**
     * The direction the stack steps in.
     *
     * Not always the slice normal: where ImageOrientationPatient describes
     * an oblique plane and ImagePositionPatient steps in some other
     * direction, consecutive positions differ by a vector with an in-plane
     * component the normal does not carry.
     */
    stepDir: vec3;
    /** In-plane voxel size, mm. */
    dx: number;
    dy: number;
    /** Spacing between slices ALONG {@link stepDir}, mm. */
    spacing: number;
    /** Centre of the first voxel of the first slice. */
    firstPosition: vec3;
    /**
     * Number of slices the stack resolves to at {@link spacing}.
     *
     * The regular grid the source is read into: `along` need not be a whole
     * multiple of the spacing on an irregular stack, so this ROUNDS, and
     * anything measuring the stack must round the same way or it describes
     * an extent the voxels do not occupy.
     */
    sliceCount: number;
    /**
     * Whole extent along {@link stepDir}, edge to edge, mm.
     *
     * `sliceCount * spacing`, so the box built from this and the grid built
     * from the count describe one volume. NOT derived from `along`: on a
     * stack whose length is not a whole multiple of its spacing — every
     * irregular stack, which is what this tool is for — the two differ by
     * up to half a spacing at the far end.
     */
    stackLengthMm: number;
}

/**
 * The geometry a stack of DICOM slices occupies, derived once.
 *
 * One derivation, not two: the spacing measured along the step direction
 * and the spacing projected onto the slice normal are the same number for
 * a transversal stack and different for the oblique ones this exists to handle,
 * so a second derivation drifts from the voxels it claims to describe.
 * Anything needing this geometry reads it here.
 */
export function sliceStackFrame(slices: DicomImageSlice[]): SliceStackFrame {
    if (slices.length === 0) {
        throw new Error('sliceStackFrame: no slices');
    }

    const first = slices[0];
    const orientation = first.imageOrientation;
    const rowDir = vec3.fromValues(
        orientation[0],
        orientation[1],
        orientation[2]
    );
    const colDir = vec3.fromValues(
        orientation[3],
        orientation[4],
        orientation[5]
    );
    const normal = vec3.fromValues(
        orientation[6],
        orientation[7],
        orientation[8]
    );

    // Ordered by the projection onto the slice normal, which is monotone
    // along the stack whatever direction it advances in.
    const ordered = slices
        .map((slice) => ({ slice, n: vec3.dot(normal, slice.imagePosition) }))
        .sort((a, b) => a.n - b.n)
        .map(({ slice }) => slice);

    const firstPosition = vec3.clone(ordered[0].imagePosition);
    const stepDir = vec3.clone(normal);
    const span = vec3.subtract(
        vec3.create(),
        ordered[ordered.length - 1].imagePosition,
        firstPosition
    );
    if (vec3.length(span) > 0) {
        vec3.normalize(stepDir, span);
    }

    const along = ordered.map((slice) =>
        vec3.dot(
            stepDir,
            vec3.subtract(vec3.create(), slice.imagePosition, firstPosition)
        )
    );

    // Coincident positions contribute a zero gap — a re-sent slice, or the
    // second echo of a dual-echo acquisition — and enough of them would
    // make the median zero, which every caller then uses as a spacing.
    const gaps: number[] = [];
    for (let i = 1; i < along.length; i++) {
        const gap = along[i] - along[i - 1];
        if (gap > 1e-3) {
            gaps.push(gap);
        }
    }
    gaps.sort((a, b) => a - b);
    const spacing =
        gaps.length > 0
            ? gaps[Math.floor(gaps.length / 2)]
            : first.sliceThickness || 1.0;

    const sliceCount = Math.max(
        1,
        Math.round(along[along.length - 1] / spacing) + 1
    );

    return {
        ordered,
        along,
        rowDir,
        colDir,
        stepDir,
        dx: first.pixelSpacing[0],
        dy: first.pixelSpacing[1],
        spacing,
        firstPosition,
        sliceCount,
        stackLengthMm: sliceCount * spacing,
    };
}

/** The frame's basis, as the direction cosines `Grid.fromDicom` expects. */
export function stackOrientation(frame: SliceStackFrame): mat3 {
    return mat3.fromValues(
        frame.rowDir[0],
        frame.rowDir[1],
        frame.rowDir[2],
        frame.colDir[0],
        frame.colDir[1],
        frame.colDir[2],
        frame.stepDir[0],
        frame.stepDir[1],
        frame.stepDir[2]
    );
}

/**
 * Why these slices cannot form one volume, or null.
 *
 * A series whose slices differ in size, in-plane spacing or orientation
 * has no single geometry to resample: writing one slice's pixels into
 * another's grid would be a silent misregistration. The check lives here
 * so the refusal the user is shown and the throw the assembler raises are
 * the same test, and a mixed-geometry series — a three-plane localizer
 * sharing one Series Instance UID — is named before anything is read
 * rather than failing halfway through.
 *
 * Slices are identified by position in the stack. A SOP Instance UID is
 * PHI, and this text is both displayed and logged.
 */
export function sliceGeometryMismatch(
    slices: DicomImageSlice[]
): string | null {
    if (slices.length === 0) {
        return 'no slices';
    }
    const first = slices[0];
    for (let index = 0; index < slices.length; index++) {
        const slice = slices[index];
        const where = `(slice ${index + 1} of ${slices.length})`;
        if (slice.cols !== first.cols || slice.rows !== first.rows) {
            return (
                `dimension mismatch: expected ${first.cols}x${first.rows}, ` +
                `got ${slice.cols}x${slice.rows} ${where}`
            );
        }
        if (
            Math.abs(slice.pixelSpacing[0] - first.pixelSpacing[0]) > 1e-3 ||
            Math.abs(slice.pixelSpacing[1] - first.pixelSpacing[1]) > 1e-3
        ) {
            return `pixel spacing mismatch ${where}`;
        }
        for (let i = 0; i < 9; i++) {
            if (
                Math.abs(
                    slice.imageOrientation[i] - first.imageOrientation[i]
                ) > 1e-3
            ) {
                return `orientation mismatch ${where}`;
            }
        }
        if (slice.pixelSpacing[0] <= 0 || slice.pixelSpacing[1] <= 0) {
            return `pixel spacing is not positive ${where}`;
        }
    }
    return null;
}
